// Work Schedule page service (PRD_WorkScheduleManagement). Pure helpers first,
// I/O last, so the page's logic is unit-testable without the emulator. Three
// contracts are load-bearing and pinned by tests: schedulePayload's keys (C3),
// readSchedule's null-for-no-working-day + seven-boolean workingDays (C2/C4),
// and draftRunCount's status semantics (C7).

import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { runIncludesEmployee } from '../utils/payrollConflicts';
import { firstWorkingDay } from '../utils/scheduleHours';
import { commitInChunks } from './adjustmentService';

export const DAY_IDS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// Step 3's defaults (AddEmployee.js:87 / :123-166): 08:00–17:00, 60-min break, Mon–Fri.
export const SCHEDULE_DEFAULTS = { startTime: '08:00', endTime: '17:00', breakDuration: 60 };
export const DEFAULT_WORKING_DAYS = {
  sunday: false, monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false,
};

// C15 (h): rule 1 reuses the wizard's string (AddEmployee.js:695); rule 2 is new.
export const NO_WORKING_DAY = 'At least one working day must be selected';
export const MISSING_TIMES = 'Working days need a start and end time.';

/**
 * The form → persisted shape (C2): every day key present, non-working days
 * carrying the same times, exactly as Step 3's all-days writer emits.
 */
export const buildSchedule = ({ workingDays, startTime, endTime, breakDuration }) =>
  Object.fromEntries(
    DAY_IDS.map((d) => [d, { isWorkingDay: !!workingDays?.[d], startTime, endTime, breakDuration }]),
  );

/**
 * The persisted shape → form (C2). Times and break come from the FIRST working
 * day (Step 3's own pick), so a non-uniform legacy doc reads the same in both
 * places. Returns null when no day is working — the "No schedule" row (C4).
 *
 * @returns {{ workingDays: Object<string, boolean>, startTime: string, endTime: string, breakDuration: number } | null}
 */
export const readSchedule = (workSchedule) => {
  const first = firstWorkingDay(workSchedule);
  if (!first) return null;
  return {
    workingDays: Object.fromEntries(DAY_IDS.map((d) => [d, workSchedule[d]?.isWorkingDay === true])),
    startTime: first.startTime || SCHEDULE_DEFAULTS.startTime,
    endTime: first.endTime || SCHEDULE_DEFAULTS.endTime,
    breakDuration: first.breakDuration ?? SCHEDULE_DEFAULTS.breakDuration,
  };
};

/** C2's two rules. @returns {string|null} the C15 (h) message, or null when valid */
export const validateSchedule = ({ workingDays, startTime, endTime }) => {
  if (!DAY_IDS.some((d) => workingDays?.[d])) return NO_WORKING_DAY;
  if (!startTime || !endTime) return MISSING_TIMES;
  return null;
};

/**
 * C3 — the ONLY place the write shape is spelled. Dotted paths, never an
 * `employment: {…}` object: for an emp:edit holder the object form would
 * replace the whole employment map (AddEmployee.js:1065-1069).
 */
export const schedulePayload = (schedule, userDocId) => ({
  'employment.workSchedule': schedule,
  'metadata.updatedAt': serverTimestamp(),
  'metadata.updatedBy': userDocId,
});

/**
 * C7 — how many DISTINCT employees (by employeeId, the id the index stores)
 * sit in at least one persisted draft run. Distinct, not a tally of entries:
 * one employee can appear in two drafts over different periods.
 *
 * @param {Array} runs - payrollIndex entries from loadActiveLocks ([] on failure)
 * @param {Array<string|number>} employeeIds
 */
export const draftRunCount = (runs, employeeIds) =>
  [...new Set(employeeIds || [])].filter((id) =>
    (runs || []).some((r) => r?.status === 'draft' && runIncludesEmployee(r, id)),
  ).length;

const employeeRef = (companyId, docId) => doc(db, 'companies', companyId, 'employees', docId);

/** Single edit (C3): one updateDoc against the employee DOC id. */
export const updateSchedule = (companyId, docId, schedule, userDocId) =>
  updateDoc(employeeRef(companyId, docId), schedulePayload(schedule, userDocId));

/**
 * Bulk assign (C3): one batch.update per employee doc id through the shared
 * 450-op chunker. Returns its { committed, failed, error }; a re-run is
 * idempotent (same values, no minted ids).
 */
export const assignScheduleBulk = (companyId, docIds, schedule, userDocId) => {
  const payload = schedulePayload(schedule, userDocId);
  return commitInChunks(
    docIds.map((id) => (batch) => batch.update(employeeRef(companyId, id), payload)),
  );
};
